import {List} from 'immutable';
import {addToast} from '@albert-io/atomic';

import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';

const DIFFICULTIES = ['easy', 'moderate', 'difficult'] as const;
type Difficulties = (typeof DIFFICULTIES)[number];

const QUESTION_TYPES = [
  'fill-in-the-blank',
  'free-entry',
  'multiple-choice',
  'text-highlight',
  'two-way',
  'snippet-selection',
  'passage-correction',
  'free-response',
  'graph-contains',
  // feature flag these
  'item-bank',
  'ordering'
] as const;
type QuestionTypes = (typeof QUESTION_TYPES)[number];

const QUESTION_SETS_QUERY_PAGE_SIZE = 25;

const difficultyToNumMap = {
  easy: 1,
  moderate: 2,
  difficult: 3
};

const numToDifficultyMap = {
  1: 'easy',
  2: 'moderate',
  3: 'difficult'
};

const constructStandardsStandardSetsFilter = (selectedStandardIds, selectedStandardSetIds) => {
  const standardsFilter =
    selectedStandardIds.length > 0
      ? {
          questions_v3: {
            standards_v1: selectedStandardIds.join(',')
          }
        }
      : {};

  const standardSetsFilter =
    selectedStandardSetIds.length > 0
      ? {
          questions_v3: {
            standards_v1: {
              standard_set_v1: selectedStandardSetIds.join(',')
            }
          }
        }
      : {};

  return [standardSetsFilter, standardsFilter];
};

const constructQuestionTypesFilter = (selectedQuestionTypes) => {
  const questionTypesFilter: Array<object> = [];
  if (!selectedQuestionTypes.isEmpty()) {
    selectedQuestionTypes.forEach((type) => {
      questionTypesFilter.push({
        questions_v3: {
          question_type: type
        }
      });
    });
  }
  return questionTypesFilter;
};

const constructAllOfFilter = (questionTypesFilter, standardsFilter, standardSetsFilter) => {
  return {
    all_of: [{any_of: [standardsFilter, standardSetsFilter]}, {any_of: [...questionTypesFilter]}]
  };
};

export interface FilterOptions {
  questionDifficulties?: List<string>;
  questionTypes?: List<string>;
  standardIds?: string[];
  standardSetIds?: string[];
  subjects?: List<SubjectModelV2>;
  guideLevels?: List<GuideLevelModelV2>;
  hideAssessment?: boolean;
  hidePreviouslyAssigned?: boolean;
  negateStandardsFilter?: boolean;
  userId?: string;
  subjectId?: string;
}

interface QueryParamFilterObject {
  question_difficulties?: string;
  question_types?: string;
  standards?: string;
  standard_sets?: string;
  subjects?: string;
  guide_levels?: string;
  hide_assessment_questions?: boolean;
  hide_adaptive_questions?: boolean;
  hide_previously_assigned?: boolean;
  negate_standards_filter?: boolean;
}

interface FilterObject {
  question_difficulties?: string[];
  question_types?: string[];
  standards?: string[];
  standard_sets?: string[];
  subjects?: string[];
  guide_levels?: string[];
  hide_assessment_questions?: boolean;
  hide_adaptive_questions?: boolean;
  hide_previously_assigned?: boolean;
  negate_standards_filter?: boolean;
  user_id?: string;
}

const maybeApplyFilterMetaContext = (query, filters: FilterOptions) => {
  const filterObject = getQueryParamFilterObject(filters);
  return query.meta({context: {content_discovery_filters: filterObject}});
};

const getQueryParamFilterObject = (filters: FilterOptions) => {
  const filterObject: QueryParamFilterObject = {};

  if (filters.questionDifficulties?.size) {
    const difficulties = filters.questionDifficulties
      .toArray()
      .map((d) => difficultyToNumMap[d])
      .join(',');
    filterObject.question_difficulties = difficulties;
  }

  if (filters.questionTypes?.size) {
    filterObject.question_types = filters.questionTypes.toArray().join(',');
  }

  if (filters.standardIds?.length) {
    filterObject.standards = filters.standardIds.join(',');
  }

  if (filters.standardSetIds?.length) {
    filterObject.standard_sets = filters.standardSetIds.join(',');
  }

  if (filters.subjects?.size) {
    filterObject.subjects = filters.subjects.map((subject) => subject.getId()).join(',');
  }

  if (filters.guideLevels?.size) {
    filterObject.guide_levels = filters.guideLevels
      .map((guideLevel) => guideLevel.getId())
      .join(',');
  }

  if (filters.hideAssessment) {
    filterObject.hide_assessment_questions = true;
  }

  if (filters.hidePreviouslyAssigned) {
    filterObject.hide_previously_assigned = true;
  }

  if (filters.negateStandardsFilter) {
    filterObject.negate_standards_filter = true;
  }

  filterObject.hide_adaptive_questions = true;

  return filterObject;
};

const getFilterObject = (filters: FilterOptions) => {
  const filterObject: FilterObject = {};

  if (filters.questionDifficulties?.size) {
    const difficulties = filters.questionDifficulties
      .toArray()
      .map((d) => difficultyToNumMap[d].toString());
    filterObject.question_difficulties = difficulties;
  }

  if (filters.questionTypes?.size) {
    filterObject.question_types = filters.questionTypes.toArray();
  }

  if (filters.standardIds?.length) {
    filterObject.standards = filters.standardIds;
  }

  if (filters.standardSetIds?.length) {
    filterObject.standard_sets = filters.standardSetIds;
  }

  if (filters.subjects?.size) {
    filterObject.subjects = filters.subjects.map((subject) => subject.getId()).toArray();
  }

  if (filters.guideLevels?.size) {
    filterObject.guide_levels = filters.guideLevels
      .map((guideLevel) => guideLevel.getId())
      .toArray();
  }

  if (filters.hideAssessment) {
    filterObject.hide_assessment_questions = true;
  }

  if (filters.hidePreviouslyAssigned) {
    filterObject.hide_previously_assigned = true;
  }

  if (filters.negateStandardsFilter) {
    filterObject.negate_standards_filter = true;
  }

  if (filters.userId) {
    filterObject.user_id = filters.userId;
  }

  filterObject.hide_adaptive_questions = true;

  return filterObject;
};

/**
 * Fetches all paginated results with automatic batch fetching
 * Always fetches page 1 first to extract metadata, then fetches remaining pages in parallel batches
 *
 * @param getPageQuery - Function that returns a query for a specific page number
 * @param totalPages - Optional total pages to override metadata (for custom pagination)
 * @param onFirstPageFetched - Callback invoked after first page with results and metadata
 * @param onProgressUpdate - Callback invoked after each batch with accumulated results
 * @param parallelBatchSize - Number of pages to fetch in parallel (default: 5)
 * @returns Promise that resolves with all results
 */
const fetchAllPaginatedResults = async ({
  getPageQuery,
  totalPages: providedTotalPages,
  onFirstPageFetched,
  onProgressUpdate,
  parallelBatchSize = 5
}: {
  getPageQuery: (page: number) => any;
  totalPages?: number;
  onFirstPageFetched?: (data: {results: any; totalPages: number; totalEntries: number}) => void;
  onProgressUpdate?: (accumulatedResults: any) => void;
  parallelBatchSize?: number;
}) => {
  try {
    // Fetch first page and extract metadata
    const firstPageQuery = getPageQuery(1);
    const results = await firstPageQuery.getResourcePromise();

    const totalPages =
      providedTotalPages ?? firstPageQuery.getResourceMetadata().getIn(['page', 'total_pages'], 1);
    const totalEntries = firstPageQuery
      .getResourceMetadata()
      .getIn(['page', 'total_entries'], results.size);

    // Notify caller with first page results
    if (onFirstPageFetched) {
      onFirstPageFetched({
        results,
        totalPages,
        totalEntries
      });
    }

    // If there are more pages, fetch them in batches
    if (totalPages > 1) {
      let accumulatedQuestions = results;

      for (let startPage = 2; startPage <= totalPages; startPage += parallelBatchSize) {
        try {
          const endPage = Math.min(startPage + parallelBatchSize - 1, totalPages);
          const pagePromises: Promise<any>[] = [];

          // Create promises for parallel batch
          for (let page = startPage; page <= endPage; page++) {
            const pageQuery = getPageQuery(page);
            pagePromises.push(pageQuery.getResourcePromise());
          }

          // Fetch batch in parallel
          const batchResults = await Promise.all(pagePromises);

          // Accumulate results
          batchResults.forEach((pageResults) => {
            accumulatedQuestions = accumulatedQuestions.concat(pageResults);
          });

          // Update progress after each batch
          if (onProgressUpdate) {
            onProgressUpdate(accumulatedQuestions);
          }
        } catch (_error) {
          addToast({
            color: 'negative',
            title: 'Error loading questions',
            message: 'Some questions failed to load. Please try again.'
          });
        }
      }

      return accumulatedQuestions;
    }

    return results;
  } catch (_error) {
    addToast({
      color: 'negative',
      title: 'Error loading questions',
      message: 'Failed to load questions. Please try again.'
    });
  }
};

export {
  maybeApplyFilterMetaContext,
  getFilterObject,
  constructAllOfFilter,
  constructQuestionTypesFilter,
  constructStandardsStandardSetsFilter,
  fetchAllPaginatedResults,
  Difficulties,
  QuestionTypes,
  DIFFICULTIES,
  QUESTION_TYPES,
  QUESTION_SETS_QUERY_PAGE_SIZE,
  numToDifficultyMap
};
